import React, {
    ChangeEvent,
    FC,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Toggle} from '@optimaxdev/design-system';

import {connect} from 'react-redux';

import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';

import {useShippingProtection} from 'desktop/pages/checkout/hooks/shippingProtection/useShippingProtection';
import {useShippingProtectionEnabled} from 'desktop/pages/checkout/hooks/shippingProtection/useShippingProtectionEnabled';
import {getOrderReviewTotalsIsLoading} from 'desktop/pages/checkout/store/selectors/orderReviewTotals/orderReviewTotals';
import {getShippingMethodsStatus} from 'desktop/pages/checkout/store/selectors/shippingMethods/shippingMethods';
import {ApplicationStoreType} from 'desktop/store';

import {ShippingProtectionIcon} from '../shippingProtectionIcon';

import {ToggleShippingContent} from './shippingContent/shippingContent';

import s from './toggleShippingProtection.scss';

export const IS_SHIPPING_PROTECTION_TOGGLED = 'isShippingProtectionToggled';

export type PropsType = {
    modalHandler: () => void;
    shippingMethodsStatus: string;
    orderReviewTotalsIsLoading: boolean;
};

/**
 * Shipping Protection toggle component
 * @returns {React.ReactElement} - Toggle Shipping Protection
 */
export const ToggleShippingProtection: FC<PropsType> = ({modalHandler}): ReactElement => {
    const [isChecked, setIsChecked] = useState(false);
    const {isLoading, onCheckboxChange, price} = useShippingProtection();
    const {isEnabled} = useShippingProtectionEnabled();

    const handleToggleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (isLoading) return;
            sendDesktopAnalyticsEvent(CHECKOUT_DELIVERY_STEP, {
                message: `Shipping Protection - Toggle ${isChecked ? 'Off' : 'On'}`,
            });
            setIsChecked(!isChecked);
            onCheckboxChange(e);
        },
        [isChecked, isLoading, onCheckboxChange],
    );

    useEffect(() => {
        setIsChecked(isEnabled);
    }, [isEnabled]);

    const storeToggle = useMemo(() => {
        return (
            <Toggle
                checked={isChecked}
                color="blue"
                disabled={isLoading}
                onChange={handleToggleChange}
                size="small"
            />
        );
    }, [handleToggleChange, isChecked, isLoading]);

    return (
        <>
            <div className={s.leftSideContainer}>
                <ShippingProtectionIcon showPackageIcon={isChecked && !isLoading} />
                <ToggleShippingContent modalHandler={modalHandler} price={price} />
            </div>
            {storeToggle}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    orderReviewTotalsIsLoading: getOrderReviewTotalsIsLoading(store),
    shippingMethodsStatus: getShippingMethodsStatus(store),
});

export const ToggleShippingProtectionConnected = connect(mapStateToProps)(ToggleShippingProtection);
